import { deriveTowelRailOutputs, getControlZonePointDemand } from "@automatry/rules";
import { NO_RISK, uniqueSourceRefs } from "./strategyContext.js";
export function buildZoneModel(projectId, apartmentTypeId, rows, rules) {
    const relevantRows = rows.filter((row) => row.apartmentTypeId === apartmentTypeId);
    if (relevantRows.length === 0) {
        throw new Error(`No takeoff rows supplied for ${apartmentTypeId}.`);
    }
    const quantity = relevantRows.reduce((total, row) => total + row.quantity, 0);
    const sourceRefs = uniqueSourceRefs([
        ...relevantRows.flatMap((row) => row.sourceRefs),
        ...rules.sourceRefs
    ]);
    const counts = relevantRows.reduce((total, row) => ({
        fcu: total.fcu + (row.fcuCount ?? 0),
        wufh: total.wufh + (row.wufhZoneCount ?? 0),
        eufh: total.eufh + (row.eufhZoneCount ?? 0),
        standaloneHeatOnly: total.standaloneHeatOnly + (row.standaloneHeatOnlyZoneCount ?? 0),
        towelRail: total.towelRail + (row.towelRailCount ?? 0),
        sourceEnable: total.sourceEnable +
            (row.hiuCount ?? 0) +
            (row.ciuCount ?? 0) +
            (row.althermaCount ?? 0)
    }), {
        fcu: 0,
        wufh: 0,
        eufh: 0,
        standaloneHeatOnly: 0,
        towelRail: 0,
        sourceEnable: 0
    });
    const logicalZones = [];
    const thermalLoads = [];
    const pointDemands = [];
    const fcuZones = [];
    const sameRoomWufhZones = [];
    const secondaryZones = [];
    const standaloneHeatOnlyZones = [];
    for (let index = 1; index <= counts.fcu; index += 1) {
        const zone = makeLogicalZone({
            apartmentTypeId,
            sourceRefs,
            zoneKind: "fcu",
            index,
            label: `FCU ${index}`,
            roomName: `FCU Room ${index}`,
            ruleTraceRefs: ["trace-fcu-heat-cool-base-points"]
        });
        const load = makeThermalLoad(zone, "fcu", "fcu_heat_cool_base");
        const pointDemand = makePointDemand(zone.id, "fcu_heat_cool_base", getControlZonePointDemand("fcu").pointDemand, sourceRefs, ["trace-fcu-heat-cool-base-points"], "FCU control zone consumes the base heat/cool point profile.", "Loaded through @automatry/rules getControlZonePointDemand('fcu').");
        fcuZones.push({
            id: zone.id,
            kind: "fcu",
            label: zone.label
        });
        logicalZones.push({ ...zone, loadRefs: [load.id] });
        thermalLoads.push(load);
        pointDemands.push(pointDemand);
    }
    for (let index = 1; index <= counts.wufh; index += 1) {
        const sameRoomFcu = fcuZones[index - 1];
        const zone = makeLogicalZone({
            apartmentTypeId,
            sourceRefs,
            zoneKind: "wufh",
            index,
            label: sameRoomFcu ? `Same-room WUFH ${index}` : `UFH-only WUFH ${index}`,
            roomName: sameRoomFcu?.label ?? `WUFH Room ${index}`,
            ...(sameRoomFcu ? { controlZoneRef: sameRoomFcu.id } : {}),
            ruleTraceRefs: ["trace-wufh-composite-zone-points"]
        });
        const load = makeThermalLoad(zone, "wufh", "wufh_composite_zone");
        const pointDemand = makePointDemand(zone.id, "wufh_composite_zone", getControlZonePointDemand("wufh").pointDemand, sourceRefs, ["trace-wufh-composite-zone-points"], "Same-room wet UFH is a composite control load with active control and limit inputs.", "Loaded through @automatry/rules getControlZonePointDemand('wufh'), preserving UI2/AO0/DO1.");
        if (sameRoomFcu) {
            sameRoomWufhZones.push({
                id: zone.id,
                kind: "wufh",
                label: zone.label,
                sameRoomAsFcuId: sameRoomFcu.id
            });
        }
        else {
            secondaryZones.push({
                id: zone.id,
                kind: "wufh",
                label: zone.label
            });
        }
        logicalZones.push({ ...zone, loadRefs: [load.id] });
        thermalLoads.push(load);
        pointDemands.push(pointDemand);
    }
    for (let index = 1; index <= counts.eufh; index += 1) {
        const zone = makeLogicalZone({
            apartmentTypeId,
            sourceRefs,
            zoneKind: "eufh",
            index,
            label: `EUFH ${index}`,
            roomName: `EUFH Room ${index}`,
            ruleTraceRefs: ["trace-eufh-load-points"]
        });
        const load = makeThermalLoad(zone, "eufh", "eufh_load");
        const pointDemand = makePointDemand(zone.id, "eufh_load_plus_control_temperature_input", getControlZonePointDemand("eufh").pointDemand, sourceRefs, ["trace-eufh-load-points"], "Electric UFH needs a load output and a separate room control temperature input.", "Loaded through @automatry/rules getControlZonePointDemand('eufh'), keeping the EUFH limit sensor out of controller UI.");
        secondaryZones.push({
            id: zone.id,
            kind: "eufh",
            label: zone.label
        });
        logicalZones.push({ ...zone, loadRefs: [load.id] });
        thermalLoads.push(load);
        pointDemands.push(pointDemand);
    }
    for (let index = 1; index <= counts.standaloneHeatOnly; index += 1) {
        const zone = makeLogicalZone({
            apartmentTypeId,
            sourceRefs,
            zoneKind: "heat_only",
            index,
            label: `Standalone heat-only ${index}`,
            roomName: `Heat-only Room ${index}`,
            ruleTraceRefs: ["trace-heat-only-zone-points"]
        });
        const load = makeThermalLoad(zone, "heat_only", "heat_only_zone");
        const pointDemand = makePointDemand(zone.id, "heat_only_zone", getControlZonePointDemand("heat_only").pointDemand, sourceRefs, ["trace-heat-only-zone-points"], "Standalone heat-only zones need a dedicated control temperature input and enable output.", "Loaded through @automatry/rules getControlZonePointDemand('heat_only').");
        standaloneHeatOnlyZones.push({
            id: zone.id,
            kind: "heat_only",
            label: zone.label
        });
        logicalZones.push({ ...zone, loadRefs: [load.id] });
        thermalLoads.push(load);
        pointDemands.push(pointDemand);
    }
    const towelRailDerivation = deriveTowelRailOutputs(counts.towelRail);
    if (counts.towelRail > 0) {
        pointDemands.push(makePointDemand(`towel-rail-loads-${apartmentTypeId}`, "towel_rail_load_relay_output", towelRailDerivation.pointDemand, sourceRefs, towelRailDerivation.traces.map((trace) => trace.id), "Towel rails are central load-relay outputs, not logical control zones.", "Derived through @automatry/rules deriveTowelRailOutputs, so they consume no controller zone slot."));
    }
    return {
        apartmentTypeId,
        quantity,
        logicalZones,
        thermalLoads,
        pointDemands,
        fcuZones,
        sameRoomWufhZones,
        secondaryZones,
        standaloneHeatOnlyZones,
        towelRailOutputCount: counts.towelRail,
        sourceEnableOutputCount: counts.sourceEnable,
        sourceRefs,
        ruleTraceRefs: [
            ...new Set([
                ...logicalZones.flatMap((zone) => zone.ruleTraceRefs),
                ...thermalLoads.flatMap((load) => load.ruleTraceRefs),
                ...pointDemands.flatMap((pointDemand) => pointDemand.ruleTraceRefs)
            ])
        ]
    };
}
function makeLogicalZone(input) {
    const id = `zone-${input.apartmentTypeId}-${input.zoneKind}-${input.index}`;
    return {
        id,
        apartmentTypeId: input.apartmentTypeId,
        label: input.label,
        roomName: input.roomName,
        zoneKind: input.zoneKind,
        ...(input.controlZoneRef ? { controlZoneRef: input.controlZoneRef } : {}),
        why: `${input.label} is present in the structured apartment takeoff.`,
        how: `Created as a ${input.zoneKind} logical zone before controller allocation.`,
        sourceRefs: input.sourceRefs,
        ruleTraceRefs: input.ruleTraceRefs,
        risk: NO_RISK
    };
}
function makeThermalLoad(zone, loadKind, profileKey) {
    return {
        id: `load-${zone.id}`,
        logicalZoneId: zone.id,
        loadKind,
        quantity: 1,
        pointProfileKey: profileKey,
        why: `${zone.label} has a ${loadKind} load to control.`,
        how: `The load uses the ${profileKey} rule profile.`,
        sourceRefs: zone.sourceRefs,
        ruleTraceRefs: zone.ruleTraceRefs,
        risk: NO_RISK
    };
}
function makePointDemand(ownerRef, demandKind, points, sourceRefs, ruleTraceRefs, why, how) {
    return {
        id: `point-demand-${ownerRef}-${demandKind}`,
        ownerRef,
        demandKind,
        points,
        consumesHardIo: true,
        activeState: "active",
        why,
        how,
        sourceRefs,
        ruleTraceRefs,
        risk: NO_RISK
    };
}
//# sourceMappingURL=zoneModel.js.map